import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  KeyRound, Loader2, CheckCircle2, AlertOctagon, Trash2,
  Eye, EyeOff, RefreshCw, Gauge, ExternalLink,
} from "lucide-react";

/**
 * Google PageSpeed Insights (PSI) — master API key page.
 *
 * Mirrors the AHREFS Master / SendGrid Master pattern. The key is
 * encrypted at rest via Fernet, only the masked hint comes back on
 * every GET. The verify button hits web.dev with a single mobile /
 * performance call — PSI is free so it's safe to click at will.
 */
export default function PsiMasterPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyDraft, setKeyDraft] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(null); // "save" | "verify" | "clear" | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/psi/master");
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load PSI state");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    if (!keyDraft.trim()) { toast.error("Paste an API key first"); return; }
    setBusy("save");
    try {
      const { data } = await api.put("/psi/master", { api_key: keyDraft.trim() });
      toast.success("PSI key saved (encrypted at rest)");
      setKeyDraft(""); setShowKey(false);
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the Google PSI master API key? Scheduled PSI runs will fall back to the low-quota anonymous pool.")) return;
    setBusy("clear");
    try {
      await api.delete("/psi/master");
      toast.success("Cleared");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(null); }
  };

  const verify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.post("/psi/master/verify");
      if (data.ok) {
        toast.success("PSI key accepted by Google");
      } else {
        toast.error(`PSI rejected: ${data.detail}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setBusy(null); }
  };

  const configured = state?.configured;

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="psi-master-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#4285F4]/10 grid place-items-center">
          <Gauge className="h-5 w-5 text-[#4285F4]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            Google PSI Master
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Agency-wide Google PageSpeed Insights API key. Powers the per-client
            PSI tab and the monthly scheduled rollup that appends a new
            data-point per property URL.
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">API key</div>
            {loading ? (
              <Badge variant="outline"
                className="ml-2 rounded-sm text-[10px] font-mono">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> loading
              </Badge>
            ) : configured ? (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-emerald-100 text-emerald-800 border-emerald-200"
                data-testid="psi-master-status-configured">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Configured
                {state?.key_hint && (
                  <span className="ml-1 opacity-70">· {state.key_hint}</span>
                )}
              </Badge>
            ) : (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-amber-100 text-amber-800 border-amber-200"
                data-testid="psi-master-status-missing">
                <AlertOctagon className="h-3 w-3 mr-1" /> Not configured
              </Badge>
            )}
          </div>

          <div>
            <Label className="label-overline">
              {configured ? "Replace / rotate key" : "Paste your Google PSI API key"}
            </Label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)}
                  placeholder="AIzaSy…"
                  className="rounded-sm font-mono text-[12px] pr-8"
                  data-testid="psi-master-key-input" />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <Button onClick={saveKey} disabled={busy !== null || !keyDraft.trim()}
                className="rounded-sm bg-[#4285F4] hover:bg-[#3367D6] text-white"
                data-testid="psi-master-save-btn">
                {busy === "save"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : "Save"}
              </Button>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-1.5 leading-relaxed">
              Get a key from{" "}
              <a href="https://developers.google.com/speed/docs/insights/v5/get-started"
                target="_blank" rel="noreferrer"
                className="text-[#4285F4] hover:underline inline-flex items-center gap-0.5">
                Google Cloud Console
                <ExternalLink className="h-2.5 w-2.5" />
              </a>{" "}
              → enable <em>PageSpeed Insights API</em> → API keys.
              PSI is free — Google doesn&apos;t bill for runs — but a key raises
              the quota from ~1&nbsp;req/sec anonymous to 25,000&nbsp;req/day.
              Encrypted with Fernet at rest, never exposed in URLs or logs.
            </p>
          </div>

          {/* Key-restriction help — the #1 support case is HTTP-referrer
              restrictions blocking our server-side calls. Surface a
              friendly, precise walkthrough directly next to the input. */}
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-3
            text-[11px] leading-relaxed">
            <div className="font-semibold text-amber-900 mb-1 flex items-center gap-1.5">
              <AlertOctagon className="h-3.5 w-3.5" />
              Getting <code className="font-mono">Requests from referer &lt;empty&gt; are blocked</code>?
            </div>
            <div className="text-amber-900 space-y-1">
              <p>
                Your key has an <strong>HTTP referrer restriction</strong> set,
                but our backend calls Google server-side (no browser referrer).
                Fix it in Google Cloud Console → <em>APIs &amp; Services</em>
                → <em>Credentials</em> → click your PSI key. Then either:
              </p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>
                  <strong>Recommended:</strong> Under <em>Application
                  restrictions</em> select <strong>None</strong> (or switch
                  to <strong>IP addresses</strong> if you want a stronger
                  lockdown).
                </li>
                <li>
                  <strong>OR</strong> keep <em>HTTP referrers</em> selected
                  and add these entries to the allowed list:
                  <div className="font-mono text-[10px] bg-white/70 rounded-sm p-1.5 mt-1">
                    *.emergentagent.com/*<br />
                    *.emergent.host/*<br />
                    icebergdigital.com.au/*<br />
                    *.icebergdigital.com.au/*
                  </div>
                </li>
                <li>
                  Under <em>API restrictions</em> make sure{" "}
                  <strong>PageSpeed Insights API</strong> is enabled.
                </li>
              </ul>
              <p className="mt-1">
                Save the key → wait ~30s for Google to propagate the change →
                click <strong>Test connection</strong> again.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
            <Button size="sm" variant="outline" onClick={verify}
              disabled={busy !== null || !configured}
              className="rounded-sm"
              data-testid="psi-master-verify-btn">
              {busy === "verify"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Test connection
            </Button>
            <Button size="sm" variant="outline" onClick={clearKey}
              disabled={busy !== null || !configured}
              className="rounded-sm text-red-600 hover:bg-red-50"
              data-testid="psi-master-clear-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear key
            </Button>
            {state?.last_verified_at && (
              <span className="ml-auto text-[10px] font-mono text-zinc-500">
                {state.last_verified_ok ? (
                  <span className="text-emerald-700">
                    <CheckCircle2 className="inline h-3 w-3 mr-0.5" />
                    Verified {new Date(state.last_verified_at).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-red-700">
                    <AlertOctagon className="inline h-3 w-3 mr-0.5" />
                    Failed {new Date(state.last_verified_at).toLocaleString()}
                  </span>
                )}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* How it works card */}
      <Card className="rounded-sm border-zinc-200 bg-zinc-50/60">
        <CardContent className="p-5 space-y-3">
          <div className="label-overline">How it works</div>
          <ul className="text-[12px] text-zinc-700 space-y-2 leading-relaxed list-disc pl-4">
            <li>
              Each client&apos;s <strong>PSI tab</strong> pulls property URLs from
              the Properties tab (auto) and lets you add extra URLs manually.
            </li>
            <li>
              A <strong>Run test now</strong> button fires one PSI request per
              strategy (mobile + desktop) × the four Lighthouse categories
              (Performance / Accessibility / Best Practices / SEO) and stores
              the results in history.
            </li>
            <li>
              A scheduled <strong>monthly rollup</strong> repeats the above
              across every client + URL — go to <a href="/scheduler"
              className="text-[#4285F4] hover:underline">/scheduler</a> to
              re-time it.
            </li>
            <li>
              The per-client PSI tab renders a trend chart so you can see
              scores improving or declining over time, plus the top
              opportunities to fix on the most recent run.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
